import { T } from '@/i18n';
import { Button } from '@/ui';
import { fmtDs, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useEffect, useRef } from 'react';
import type { HourGap } from './staffing';

export function DayDetails({
  date,
  planned,
  need,
  gaps,
  budget,
  onDay,
  onNew,
  onClose,
}: {
  date: string;
  planned: number;
  need: number | null;
  gaps: HourGap[];
  budget?:
    | {
        cost: number;
        sales: number | null;
        source: string;
        own: number | null;
        past: boolean;
        onSave: (value: number | null) => void;
      }
    | undefined;
  onDay: () => void;
  onNew?: ((gap?: HourGap) => void) | undefined;
  onClose: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a different day moves focus to its heading
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
  }, [date]);
  return (
    <aside className="vpanel" aria-labelledby="day-details-title" data-testid="day-details">
      <div className="vphd">
        <div>
          <h3 id="day-details-title" tabIndex={-1} ref={heading}>
            {T('Bemanning')} · {fmtDs(date)}
          </h3>
          <span>{T('Hele teamet · denne dagen')}</span>
        </div>
        <button type="button" className="x" aria-label={T('Lukk')} onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <dl className="vfacts">
        <div>
          <dt>{T('Planlagte timer før pause')}</dt>
          <dd>{num(planned)} t</dd>
        </div>
        <div>
          <dt>{T('Timebehov')}</dt>
          <dd>{need == null ? T('Ikke oppgitt') : `${num(need)} t`}</dd>
        </div>
      </dl>
      <p className="hint">
        {T(
          'Summen av timer er ikke det samme som dekning gjennom dagen. Tidspunktene under tar hensyn til planlagte pauser.',
        )}
      </p>
      <div className="vgroup">
        <h4>{T('Tidspunkter som mangler folk')}</h4>
        {gaps.length ? (
          gaps.map((g) => (
            <div className="vcand" key={`${g.from}-${g.to}`}>
              <span>
                <b>
                  {g.from}–{g.to}
                </b>
                <small>{T('{have} av {need} på jobb', { have: g.have, need: g.need })}</small>
              </span>
              {onNew && (
                <Button small onClick={() => onNew(g)}>
                  <Icon name="plus" />
                  {T('Legg til vakt')}
                </Button>
              )}
            </div>
          ))
        ) : (
          <p>
            {need == null
              ? T('Sett bemanningsbehov for å sammenligne.')
              : T('Ingen udekkede tidsrom funnet.')}
          </p>
        )}
      </div>
      {budget && (
        <details className="vdisc">
          <summary>{T('Budsjett og omsetning')}</summary>
          <dl className="vfacts">
            <div>
              <dt>{T('Planlagt lønnskostnad')}</dt>
              <dd>{num(Math.round(budget.cost))} kr</dd>
            </div>
            <div>
              <dt>{budget.source}</dt>
              <dd>{budget.sales == null ? '–' : `${num(budget.sales)} kr`}</dd>
            </div>
            {budget.sales != null && budget.sales > 0 && (
              <div>
                <dt>{T('Lønnsandel')}</dt>
                <dd>{num(Math.round((budget.cost / budget.sales) * 1000) / 10)} %</dd>
              </div>
            )}
          </dl>
          <label className="fld2">
            <span>{budget.past ? T('Omsetning') : T('Forventet omsetning')}</span>
            <input
              className="inp"
              type="number"
              min={0}
              step={100}
              key={`${date}-${budget.own}`}
              defaultValue={budget.own ?? ''}
              onBlur={(e) => {
                const v = e.target.value === '' ? null : Number(e.target.value);
                if (v !== budget.own) budget.onSave(v);
              }}
            />
          </label>
          <p className="hint">{T('Omsetning ekskl. mva. Lagres når du går ut av feltet.')}</p>
        </details>
      )}
      <div className="vfoot">
        <Button onClick={onDay}>
          <Icon name="calendar" />
          {T('Åpne dagsplan')}
        </Button>
        {onNew && (
          <Button tone="pri" onClick={() => onNew()}>
            <Icon name="plus" />
            {T('Ny vakt')}
          </Button>
        )}
      </div>
    </aside>
  );
}
